import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  useParams
} from 'react-router-dom'
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map
} from '@vis.gl/react-google-maps'
import {
  getStayOptionsApiV1StaysOptionsTokenGet,
  selectStayOptionApiV1StaysOptionsTokenSelectPost
} from '../client'
import type {
  StayMapOption,
  StayOptionSetResponse,
  StayPhoto,
  StayRoomOption,
  StaySearchParams
} from '../client'
import {
  StayEvents,
  trackEvent
} from '../analytics/posthog'
import {
  API_BASE_URL
} from '../config'
import {
  getApiErrorStatus
} from '../services/api'
import {
  CookieConsentBanner
} from './CookieConsentBanner'
import {
  OptionSetUnavailable
} from './OptionSetUnavailable'
import {
  optionSetLoadFailure
} from '../utils/optionSetLoad'
import type {
  OptionSetLoadFailure
} from '../utils/optionSetLoad'

// @ts-ignore - Vite env types
const MAPS_API_KEY = (import.meta.env?.VITE_GOOGLE_MAPS_API_KEY as string | undefined) || ''
// AdvancedMarker requires a cloud map ID. DEMO_MAP_ID is Google's sample
// placeholder: it renders and supports Advanced Markers, but Google documents
// it as not intended for production and it cannot use cloud-based styling.
// Setting the VITE_GOOGLE_MAPS_MAP_ID repo variable to a real map ID replaces
// it everywhere; until then this page ships with Google's default styling.
// @ts-ignore - Vite env types
const MAPS_MAP_ID = (import.meta.env?.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) || 'DEMO_MAP_ID'

type LoadState = 'loading' | OptionSetLoadFailure | 'ready'

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(price)
  } catch {
    return `${currency} ${Math.round(price)}`
  }
}

function formatExactPrice(price: number, currency: string): string {
  // Small amounts (a €5.53 city tax) must keep their cents — `formatPrice`
  // rounds to whole units, which is right for a stay total and wrong here.
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency
    }).format(price)
  } catch {
    return `${currency} ${price.toFixed(2)}`
  }
}

function formatStayDates(params: StaySearchParams | null | undefined): string | null {
  // The page used to state the dates only inside the free-text search summary,
  // which is a 200-char truncation of the brief — so a user comparing two date
  // ranges across two links had no way to tell which link was which (GET-10085).
  if (!params?.checkin || !params?.checkout) {
    return null
  }
  // `T00:00:00` pins these to LOCAL midnight. A bare `YYYY-MM-DD` parses as UTC
  // midnight, which renders as the previous day for any negative UTC offset —
  // so this deliberately does NOT reuse `formatCancelDate`, which takes a full
  // ISO datetime and would need a lossy round-trip through `toISOString()`.
  const start = new Date(`${params.checkin}T00:00:00`)
  const end = new Date(`${params.checkout}T00:00:00`)
  const fmt = (date: Date, iso: string): string =>
    Number.isNaN(date.getTime())
      ? iso
      : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  const nights = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  const nightPart = nights > 0 ? ` · ${nights} night${nights === 1 ? '' : 's'}` : ''
  // Count children too — a 2-adult + 1-child search must not render "2 guests"
  // in the header the whole page is anchored on.
  const people = (params.adults ?? 0) + (params.children ?? 0)
  const guests = people > 0 ? ` · ${people} guest${people === 1 ? '' : 's'}` : ''
  return `${fmt(start, params.checkin)} – ${fmt(end, params.checkout)}${nightPart}${guests}`
}

function formatDistance(km: number): string {
  // Sub-kilometre distances are the whole point in a dense city — "0.3 km"
  // reads as rounding noise where "300 m" reads as walkable.
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`
}

function distanceLabel(option: StayMapOption): string | null {
  // The anchor is per-option, not per-page: a brief naming several acceptable
  // hubs runs one anchored search per hub, so one shortlist legitimately mixes
  // them. Without the name, "1.3 km away" is a number the user can't act on —
  // away from the airport, the venue, the city centre?
  if (option.distance_km == null) {
    return null
  }
  const distance = formatDistance(option.distance_km)
  return option.distance_anchor
    ? `${distance} from ${option.distance_anchor}`
    : `${distance} away`
}

function priceLabel(option: StayMapOption): string {
  const min = option.price_min ?? option.price
  const max = option.price_max ?? option.price
  return max > min
    ? `${formatPrice(min, option.currency)} – ${formatPrice(max, option.currency)}`
    : formatPrice(min, option.currency)
}

function formatCancelDate(iso: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  }).format(date)
}

function refundPolicy(room: StayRoomOption): string {
  if (!room.refundable) {
    return 'Non-refundable'
  }
  const until = room.free_cancel_until ? formatCancelDate(room.free_cancel_until) : null
  return until ? `Free cancellation until ${until}` : 'Free cancellation'
}

interface RoomMatrixData {
  roomNames: string[]
  boardNames: string[]
  // cells[roomName][boardName] = the rate(s) for that pairing, guest-total
  // cheapest first. Per refund policy we keep the offer that costs least to
  // stay in AND — only when that is a different offer — the one with the lowest
  // RATE, so the refundable/non-refundable choice stays visible and the rate the
  // card headlines as `price_min` always appears here too. At most two rows per
  // refund policy, and the second one always carries a fee line explaining why
  // it sorts below a higher-looking rate.
  cells: Record<string, Record<string, StayRoomOption[]>>
}

// What the guest actually pays: the rate plus any taxes/fees the property
// collects at check-in. Mirrors the backend's `guest_total` (GET-11449) — the
// backend picks the headline room this way, so ranking the expanded matrix on
// the bare rate would contradict the very card the user tapped. Falls back to
// the rate when the fee is unknown, or is quoted in another currency (adding
// those would invent a total out of an exchange rate); both are lower bounds.
function guestTotal(room: StayRoomOption): number {
  const extra = room.excluded_taxes_and_fees
  if (extra === null || extra === undefined) {
    return room.price
  }
  const feeCurrency = room.excluded_taxes_currency
  if (feeCurrency !== null && feeCurrency !== undefined
    && feeCurrency !== room.currency) {
    return room.price
  }
  return room.price + extra
}

// Pivot the flat offer list into a room x board grid so every price is visible
// at a glance. First-seen room/board order is preserved.
export function buildRoomMatrix(rooms: StayRoomOption[]): RoomMatrixData {
  const roomNames: string[] = []
  const boardNames: string[] = []
  const cells: Record<string, Record<string, StayRoomOption[]>> = {}
  const collected: Record<string, Record<string, StayRoomOption[]>> = {}
  for (const room of rooms) {
    const roomName = room.room_name ?? 'Room'
    const boardName = room.board_name ?? 'Room only'
    if (!roomNames.includes(roomName)) {
      roomNames.push(roomName)
    }
    if (!boardNames.includes(boardName)) {
      boardNames.push(boardName)
    }
    const row = collected[roomName] ?? {}
    collected[roomName] = row
    const rates = row[boardName] ?? []
    row[boardName] = rates
    rates.push(room)
  }

  // Per refund policy, keep the offer that costs least to actually stay in AND
  // — when that is a different offer — the one with the lowest RATE. Keeping
  // only the guest-total winner would drop the very rate the card headlines as
  // `price_min` / "Cheapest room", leaving the user a price that appears
  // nowhere in the matrix (GET-11449). Both rows sort by guest total, so the
  // lower-rate one renders below with its `+ X at property` line saying why.
  for (const roomName of roomNames) {
    for (const boardName of boardNames) {
      const group = collected[roomName]?.[boardName]
      if (!group) {
        continue
      }
      const kept: StayRoomOption[] = []
      for (const refundable of [true, false]) {
        const kind = group.filter((rate) => rate.refundable === refundable)
        if (kind.length === 0) {
          continue
        }
        const bestTotal = kind.reduce((a, b) => (guestTotal(b) < guestTotal(a) ? b : a))
        const bestRate = kind.reduce((a, b) => (b.price < a.price ? b : a))
        kept.push(bestTotal)
        if (bestRate.offer_id !== bestTotal.offer_id) {
          kept.push(bestRate)
        }
      }
      kept.sort((a, b) => guestTotal(a) - guestTotal(b))
      const row = cells[roomName] ?? {}
      cells[roomName] = row
      row[boardName] = kept
    }
  }
  return { roomNames, boardNames, cells }
}

// Trim LiteAPI's verbose board labels so they fit the matrix header.
function shortBoard(board: string): string {
  return board.replace(/ included$/i, '').trim()
}

// The at-property charge for a matrix cell, or null when there is nothing to
// add. The matrix ORDERS on the guest total (see `guestTotal`) but each cell
// shows the rate, so without this a dearer-looking rate can sit above a cheaper
// one for no visible reason — and a guest can pick a rate whose real cost is
// disclosed nowhere on the page. `0` is "we checked, nothing extra"; null/
// undefined is "we cannot say", and neither earns a line.
export function atPropertyLabel(room: StayRoomOption): string | null {
  const extra = room.excluded_taxes_and_fees
  if (extra === null || extra === undefined || extra <= 0) {
    return null
  }
  // Exact, not rounded: a €5.53 city tax must not render as "+ €6" while the
  // card's own note says €5.53, and a sub-50c fee must not render as "+ €0",
  // which reads as "nothing extra" — the one thing this label must never say.
  const amount = formatExactPrice(extra, room.excluded_taxes_currency ?? room.currency)
  return `+ ${amount} at property`
}

// Compact per-rate cancellation label for a matrix cell, including the date.
function refundLabel(room: StayRoomOption): string {
  if (!room.refundable) {
    return 'non-ref'
  }
  const until = room.free_cancel_until ? formatCancelDate(room.free_cancel_until) : null
  return until ? `✓ free · ${until}` : '✓ free'
}

function googlePhotoUrl(photo: StayPhoto, width: number): string | null {
  if (!MAPS_API_KEY) {
    return null
  }
  // Place Photos (New) media endpoint; loaded client-side with the browser key.
  return `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=${width}&key=${MAPS_API_KEY}`
}

// Prefer Google's place photo (with required attribution) over the LiteAPI
// image; fall back to a placeholder when neither is available.
function HotelImage({
  option,
  width,
  wrapperClass
}: {
  option: StayMapOption
  width: number
  wrapperClass: string
}): JSX.Element {
  const googlePhoto = option.google_photos?.[0]
  const googleUrl = googlePhoto ? googlePhotoUrl(googlePhoto, width) : null
  const src = googleUrl ?? option.main_photo ?? null

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-catch-beige text-2xl ${wrapperClass}`}>
        🏨
      </div>
    )
  }
  return (
    <div className={`relative overflow-hidden ${wrapperClass}`}>
      <img src={src} alt={option.name} loading="lazy" className="h-full w-full object-cover" />
      {googleUrl && googlePhoto?.attribution && (
        <span
          title={`© ${googlePhoto.attribution}`}
          className="absolute inset-x-0 bottom-0 block truncate bg-black/40 px-1 text-[9px] leading-tight text-white"
        >
          © {googlePhoto.attribution}
        </span>
      )}
    </div>
  )
}

function googleMapsHref(option: StayMapOption): string {
  // Query by name + address so Google opens the hotel's place card (reviews,
  // photos, info) rather than dropping a pin at bare coordinates.
  const query = [option.name, option.address, option.city]
    .filter(Boolean)
    .join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

function Stars({ stars }: { stars: number | null | undefined }): JSX.Element | null {
  if (stars == null) {
    return null
  }
  const rounded = Math.round(stars)
  return (
    <span className="text-catch-red" aria-label={`${rounded} star`}>
      {'★'.repeat(rounded)}
    </span>
  )
}

function PriceComparison({ option }: { option: StayMapOption }): JSX.Element | null {
  // LiteAPI returns what the SAME offer sells for on a mainstream OTA, source
  // attached (observed: booking.com). It only reaches us when it beats our
  // price, so its presence means there is a real saving.
  //
  // The benchmark describes the CHEAPEST room specifically, but the card
  // headlines a min-max range — so an unqualified "Save 20%" next to
  // "€142 – €189" is false for anyone who books the €189 room, which costs
  // MORE than the €177 struck through. Name the room the comparison is about
  // whenever the card shows a range; a money claim the user can misattribute
  // is worse than no claim.
  const ota = option.suggested_selling_price
  const source = option.suggested_selling_source
  const ours = option.price_min ?? option.price
  if (ota == null || ota <= ours) {
    return null
  }
  const saving = Math.round(((ota - ours) / ota) * 100)
  if (saving < 1) {
    return null
  }
  const isRange = option.price_max != null && option.price_max > ours
  const subject = isRange
    ? `Cheapest room ${formatPrice(ours, option.currency)}`
    : 'This stay'
  return (
    <p className="mt-1 text-[11px] leading-tight text-gray-600">
      {subject} — {source ?? 'elsewhere'} shows{' '}
      <span className="line-through tabular-nums">
        {formatPrice(ota, option.currency)}
      </span>
      , <span className="font-medium text-green-700">{saving}% less here</span>
    </p>
  )
}

function UsdPriceNote({
  option,
  showsUsd
}: {
  option: StayMapOption
  showsUsd: boolean | null | undefined
}): JSX.Element | null {
  // Catch pays the property in its own currency and bills the card in USD, so
  // a £928 stay reaches the statement as ~$1,245 — a number the user would
  // otherwise never see before it was charged.
  //
  // Hedged, unlike the confirmation and the emails: nothing is booked yet, so
  // this figure is today\'s rate rather than the one fixed on the row at
  // booking. Same-day it is exactly what gets charged; across days it can drift
  // a fraction of a percent, and promising it outright would be a promise we
  // have not made yet.
  const usd = option.price_usd
  if (usd == null || !showsUsd) {
    return null
  }
  return (
    <p className="mt-1 text-[11px] leading-tight text-gray-500">
      About {formatPrice(usd, 'USD')} on your card
    </p>
  )
}

function ExcludedTaxesNote({ option }: { option: StayMapOption }): JSX.Element | null {
  // Money the PROPERTY collects at check-in (e.g. the French taxe de sejour, a
  // resort fee) and that is NOT in the total shown above — leaving it
  // undisclosed turns a genuine saving into a surprise at the desk. `0` means
  // "breakdown seen, nothing extra"; `null` means LiteAPI told us nothing, so
  // we stay silent rather than imply there is nothing to pay.
  //
  // Deliberately "taxes and fees", not "taxes": the excluded lines include
  // resort and cleaning fees, and telling someone "tax" then charging a resort
  // fee is a promise we didn't keep.
  const extra = option.excluded_taxes_and_fees
  if (extra == null || extra <= 0) {
    return null
  }
  return (
    <p className="mt-1 text-[11px] text-gray-500">
      + {formatExactPrice(extra, option.excluded_taxes_currency ?? option.currency)}
      {' '}in taxes and fees, paid at the property
    </p>
  )
}

function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-catch-white text-catch-black font-sans">
      {children}
      <CookieConsentBanner storageKey="stays_cookie_consent" />
    </div>
  )
}

export function StayOptionsPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<LoadState>('loading')
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [data, setData] = useState<StayOptionSetResponse | null>(null)
  const [activeOfferId, setActiveOfferId] = useState<string | null>(null)
  const [selectingOfferId, setSelectingOfferId] = useState<string | null>(null)
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [alreadyTaken, setAlreadyTaken] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set())

  const toggleExpanded = useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    async function load(): Promise<void> {
      if (!token) {
        setState('expired')
        return
      }
      setState('loading')
      const res = await getStayOptionsApiV1StaysOptionsTokenGet({
        path: { token },
        baseURL: API_BASE_URL,
        throwOnError: false
      })
      if (cancelled) {
        return
      }
      if (res.error || !res.data) {
        const status = getApiErrorStatus(res)
        const failure = optionSetLoadFailure(status)
        setState(failure)
        trackEvent(StayEvents.OPTIONS_UNAVAILABLE, {
          token, failure, status
        })
        return
      }
      const result = res.data
      setData(result)
      setAlreadyTaken(result.already_selected ?? false)
      setState('ready')
      trackEvent(StayEvents.OPTIONS_VIEWED, {
        token,
        options: result.options.length
      })
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token, loadAttempt])

  const retryLoad = useCallback(() => {
    setLoadAttempt((attempt) => attempt + 1)
  }, [])

  const handleSelect = useCallback(
    async (offerId: string): Promise<void> => {
      if (!token || selectingOfferId || selectedName || alreadyTaken) {
        return
      }
      setSelectingOfferId(offerId)
      const res = await selectStayOptionApiV1StaysOptionsTokenSelectPost({
        path: { token },
        body: { offer_id: offerId },
        baseURL: API_BASE_URL,
        throwOnError: false
      })
      setSelectingOfferId(null)
      if (res.error) {
        // A 409 means someone already picked from this set (or a double-tap);
        // other errors are transient — leave the list so the user can retry.
        if (res.response?.status === 409) {
          setAlreadyTaken(true)
          trackEvent(StayEvents.OPTION_ALREADY_TAKEN, { offer_id: offerId })
        }
        return
      }
      setSelectedName(res.data?.hotel_name ?? 'your pick')
      trackEvent(StayEvents.OPTION_CHOSEN, {
        offer_id: offerId,
        hotel_name: res.data?.hotel_name
      })
    },
    [token, selectingOfferId, selectedName, alreadyTaken]
  )

  const mapCenter = useMemo(() => {
    const located = (data?.options ?? []).filter(
      (o) => o.latitude != null && o.longitude != null
    )
    if (located.length === 0) {
      return null
    }
    const lat = located.reduce((sum, o) => sum + (o.latitude as number), 0) / located.length
    const lng = located.reduce((sum, o) => sum + (o.longitude as number), 0) / located.length
    return { lat, lng }
  }, [data])

  // Depends only on the fetched payload, but the render body re-runs on every
  // marker tap and every room-list toggle. Must sit above the early returns —
  // a hook called after one would break the Rules of Hooks.
  const stayDates = useMemo(
    () => formatStayDates(data?.search_params),
    [data?.search_params]
  )

  if (state === 'loading') {
    return (
      <PageShell>
        <div className="flex min-h-screen items-center justify-center text-gray-500">
          Loading your options…
        </div>
      </PageShell>
    )
  }

  if (state === 'expired' || state === 'unreachable' || !data) {
    return (
      <PageShell>
        <OptionSetUnavailable
          failure={state === 'unreachable' ? 'unreachable' : 'expired'}
          onRetry={retryLoad}
          className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center"
        />
      </PageShell>
    )
  }

  if (selectedName) {
    return (
      <PageShell>
        <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center animate-fade-in">
          <div className="text-5xl">🎉</div>
          <h1 className="mt-4 font-display text-3xl">Nice pick</h1>
          <p className="mt-3 text-gray-600">
            I&apos;ve let your assistant know you like <strong>{selectedName}</strong>.
            Head back to your chat and it&apos;ll take it from there.
          </p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <header className="border-b border-catch-beige-dark px-6 py-5">
        <h1 className="font-display text-2xl sm:text-3xl">Places to stay</h1>
        {stayDates && (
          <p className="mt-1 font-sans text-sm font-medium text-catch-black">
            {stayDates}
          </p>
        )}
        {data.search_summary && (
          <p className="mt-1 text-sm text-gray-600">{data.search_summary}</p>
        )}
        {alreadyTaken && (
          <p className="mt-2 text-sm text-catch-red">
            You&apos;ve already picked one of these — check your chat to keep going.
          </p>
        )}
      </header>

      <div className="flex flex-col-reverse lg:h-[calc(100vh-89px)] lg:flex-row">
        <div className="lg:w-[440px] lg:shrink-0 lg:overflow-y-auto">
          <ul className="divide-y divide-catch-beige-dark">
            {data.options.map((option) => (
              <li key={option.offer_id}>
                <OptionCard
                  option={option}
                  showsUsd={data.shows_usd_prices}
                  isActive={activeOfferId === option.offer_id}
                  expanded={expandedKeys.has(option.offer_id)}
                  selectingOfferId={selectingOfferId}
                  disabled={alreadyTaken || selectingOfferId !== null}
                  onHover={() => setActiveOfferId(option.offer_id)}
                  onToggle={() => {
                    if (!expandedKeys.has(option.offer_id)) {
                      trackEvent(StayEvents.LISTING_EXPANDED, { hotel: option.name })
                    }
                    toggleExpanded(option.offer_id)
                  }}
                  onChoose={(offerId) => void handleSelect(offerId)}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="h-60 lg:h-auto lg:flex-1">
          <OptionsMap
            options={data.options}
            center={mapCenter}
            activeOfferId={activeOfferId}
            onActivate={setActiveOfferId}
            onChoose={(offerId) => void handleSelect(offerId)}
            choosingDisabled={alreadyTaken || selectingOfferId !== null}
          />
        </div>
      </div>

      <footer className="px-6 py-4 text-center text-xs text-gray-400">
        Powered by Catch · prices are indicative and confirmed at booking
        {/* "300 m from Gare de Lyon" reads as a walk; it's a straight line, and
            the real walk is longer. Disclosed once here rather than appended to
            every card, matching how the price caveat above is handled. */}
        {data.options.some((o) => o.distance_km != null)
          && ' · distances are straight-line'}
      </footer>
    </PageShell>
  )
}

function OptionCard({
  option,
  showsUsd,
  isActive,
  expanded,
  selectingOfferId,
  disabled,
  onHover,
  onToggle,
  onChoose
}: {
  option: StayMapOption
  showsUsd: boolean | null | undefined
  isActive: boolean
  expanded: boolean
  selectingOfferId: string | null
  disabled: boolean
  onHover: () => void
  onToggle: () => void
  onChoose: (offerId: string) => void
}): JSX.Element {
  // Fall back to a single row built from the lead offer if a listing has no
  // room breakdown, so "Choose" always works.
  const rooms: StayRoomOption[] = (option.room_options ?? []).length > 0
    ? option.room_options ?? []
    : [{
      offer_id: option.offer_id,
      room_name: null,
      board_name: null,
      price: option.price,
      currency: option.currency,
      refundable: option.refundable,
      free_cancel_until: null
    }]
  const matrix = buildRoomMatrix(rooms)

  return (
    <div className={`transition-colors ${isActive ? 'bg-catch-grey' : ''}`}>
      <button
        type="button"
        onClick={onToggle}
        onMouseEnter={onHover}
        aria-expanded={expanded}
        className="flex w-full items-start gap-4 p-4 text-left"
      >
        <HotelImage
          option={option}
          width={240}
          wrapperClass="h-24 w-24 shrink-0 rounded-xl"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h2 className="truncate font-display text-base font-medium">{option.name}</h2>
            <div className="shrink-0 text-right">
              <div className="whitespace-nowrap font-sans text-sm font-semibold tabular-nums">
                {priceLabel(option)}
              </div>
              <div className="text-[11px] text-gray-500">
                {option.excluded_taxes_and_fees != null
                  && option.excluded_taxes_and_fees > 0
                  ? 'total stay, before local taxes'
                  : 'total stay'}
              </div>
            </div>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-600">
            <Stars stars={option.stars} />
            {/* `/10` is explicit because the 0-5 star glyphs sit right beside it. */}
            {option.rating != null && (
              <span>
                {option.rating.toFixed(1)}/10
                {option.review_count != null && ` (${option.review_count})`}
              </span>
            )}
            {distanceLabel(option) && (
              <span className="text-gray-700">{distanceLabel(option)}</span>
            )}
            {option.refundable && <span className="text-green-700">Refundable</span>}
          </div>
          <PriceComparison option={option} />
          <ExcludedTaxesNote option={option} />
          <UsdPriceNote option={option} showsUsd={showsUsd} />
          {option.rationale && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-700">{option.rationale}</p>
          )}
          <div className="mt-1.5 text-xs font-medium text-catch-red">
            {expanded
              ? 'Hide rooms ▴'
              : `View ${matrix.roomNames.length} room option${matrix.roomNames.length === 1 ? '' : 's'} ▾`}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 animate-fade-in">
          <HotelImage option={option} width={800} wrapperClass="h-40 w-full rounded-xl" />
          <a
            href={option.google_maps_url ?? googleMapsHref(option)}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent(StayEvents.OPEN_IN_GOOGLE_MAPS, { hotel: option.name })}
            className="mt-2 inline-block text-sm text-gray-500 underline hover:text-catch-black"
          >
            Open in Google Maps
          </a>
          <RoomMatrix
            matrix={matrix}
            selectingOfferId={selectingOfferId}
            disabled={disabled}
            onChoose={onChoose}
          />
        </div>
      )}
    </div>
  )
}

function RoomMatrix({
  matrix,
  selectingOfferId,
  disabled,
  onChoose
}: {
  matrix: RoomMatrixData
  selectingOfferId: string | null
  disabled: boolean
  onChoose: (offerId: string) => void
}): JSX.Element {
  const { roomNames, boardNames, cells } = matrix
  return (
    <div className="mt-3">
      <div className="overflow-x-auto rounded-xl border border-catch-beige-dark">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-catch-grey">
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Room</th>
              {boardNames.map((board) => (
                <th
                  key={board}
                  className="whitespace-nowrap px-1.5 py-1.5 text-center font-medium text-gray-500"
                >
                  {shortBoard(board)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roomNames.map((roomName) => (
              <tr key={roomName} className="border-t border-catch-beige-dark/70">
                <td className="px-2 py-1.5 align-middle font-medium">{roomName}</td>
                {boardNames.map((board) => {
                  const rates = cells[roomName]?.[board]
                  return (
                    <td key={board} className="px-1 py-1 align-middle">
                      {rates && rates.length > 0
                        ? (
                          <div className="space-y-1">
                            {rates.map((rate) => {
                              const atProperty = atPropertyLabel(rate)
                              return (
                              <button
                                key={rate.offer_id}
                                type="button"
                                onClick={() => onChoose(rate.offer_id)}
                                disabled={disabled}
                                title={refundPolicy(rate)}
                                className="block w-full whitespace-nowrap rounded-md border border-catch-beige-dark px-1.5 py-1 transition-colors hover:border-catch-red disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <span className="block text-sm font-semibold tabular-nums">
                                  {selectingOfferId === rate.offer_id
                                    ? '…'
                                    : formatPrice(rate.price, rate.currency)}
                                </span>
                                {atProperty !== null && (
                                  <span className="block text-[10px] leading-tight text-gray-500 tabular-nums">
                                    {atProperty}
                                  </span>
                                )}
                                <span
                                  className={`block text-[10px] leading-tight ${
                                    rate.refundable ? 'text-green-700' : 'text-gray-400'
                                  }`}
                                >
                                  {refundLabel(rate)}
                                </span>
                              </button>
                              )
                            })}
                          </div>
                          )
                        : (
                          <span className="block text-center text-gray-300">—</span>
                          )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[11px] text-gray-400">
        Tap a price to choose that room &amp; board.
      </p>
    </div>
  )
}

function OptionsMap({
  options,
  center,
  activeOfferId,
  onActivate,
  onChoose,
  choosingDisabled
}: {
  options: StayMapOption[]
  center: { lat: number, lng: number } | null
  activeOfferId: string | null
  onActivate: (offerId: string | null) => void
  onChoose: (offerId: string) => void
  choosingDisabled: boolean
}): JSX.Element {
  const located = options.filter((o) => o.latitude != null && o.longitude != null)
  const active = located.find((o) => o.offer_id === activeOfferId) ?? null

  // Frame the map to all pins when there's more than one; otherwise centre on
  // the single option. Auto-fit beats a guessed zoom across cities/spreads.
  const bounds = located.length > 1
    ? located.reduce(
      (b, o) => ({
        north: Math.max(b.north, o.latitude as number),
        south: Math.min(b.south, o.latitude as number),
        east: Math.max(b.east, o.longitude as number),
        west: Math.min(b.west, o.longitude as number)
      }),
      { north: -90, south: 90, east: -180, west: 180 }
    )
    : null

  if (!MAPS_API_KEY) {
    return (
      <div className="flex h-full items-center justify-center bg-catch-beige px-6 text-center text-sm text-gray-500">
        Map preview is unavailable here. Use “Open in Google Maps” on any option
        to see where it is.
      </div>
    )
  }

  if (!center) {
    return (
      <div className="flex h-full items-center justify-center bg-catch-beige px-6 text-center text-sm text-gray-500">
        We don&apos;t have map locations for these options.
      </div>
    )
  }

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
      <Map
        {...(bounds
          ? { defaultBounds: { ...bounds, padding: 64 } }
          : { defaultCenter: center, defaultZoom: 14 })}
        mapId={MAPS_MAP_ID}
        gestureHandling="greedy"
        disableDefaultUI
        className="h-full w-full"
      >
        {located.map((option) => (
          <AdvancedMarker
            key={option.offer_id}
            position={{ lat: option.latitude as number, lng: option.longitude as number }}
            onClick={() => onActivate(option.offer_id)}
          >
            <div
              className={`rounded-full border px-2 py-1 text-xs font-semibold shadow-soft ${
                activeOfferId === option.offer_id
                  ? 'border-catch-red bg-catch-red text-catch-white'
                  : 'border-catch-beige-dark bg-catch-white text-catch-black'
              }`}
            >
              {formatPrice(option.price_min ?? option.price, option.currency)}
            </div>
          </AdvancedMarker>
        ))}

        {active && active.latitude != null && active.longitude != null && (
          <InfoWindow
            position={{ lat: active.latitude, lng: active.longitude }}
            onCloseClick={() => onActivate(null)}
            pixelOffset={[0, -36]}
          >
            <div className="max-w-[220px] font-sans">
              <div className="font-medium">{active.name}</div>
              <div className="mt-0.5 text-sm text-gray-600">
                {priceLabel(active)} total
              </div>
              {distanceLabel(active) && (
                <div className="text-xs text-gray-600">{distanceLabel(active)}</div>
              )}
              <ExcludedTaxesNote option={active} />
              <button
                type="button"
                onClick={() => onChoose(active.offer_id)}
                disabled={choosingDisabled}
                className="mt-2 rounded-full bg-catch-red px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
              >
                Choose this
              </button>
            </div>
          </InfoWindow>
        )}
      </Map>
    </APIProvider>
  )
}
